import { useCallback, useEffect, useState } from "react";
import { useBrand } from "@/contexts/BrandContext/hooks";
import {
  claimAutoPrompt,
  hasOtherAutoPromptShown,
  isAutoPromptActive,
  isOverlayOpen,
  useAutoPromptRelease,
} from "@/lib/autoPrompt";

const PROMPT_ID = "founder-call";
const DAY_MS = 24 * 60 * 60 * 1000;
const ELIGIBLE_MS = 30 * DAY_MS; // a brand's first month
const SNOOZE_MS = 3 * DAY_MS; // "Maybe later"
// The page must sit with nothing else open this long before the prompt shows.
// It also lets the review and dev-store prompts (2–3s) take their turn first.
const QUIET_MS = 5000;
const CHECK_INTERVAL_MS = 1000;

interface StoredState {
  booked?: boolean;
  snoozedUntil?: number;
}

const storageKey = (brandId: string) => `founder_call_prompt:${brandId}`;

function readState(brandId: string): StoredState {
  try {
    return JSON.parse(localStorage.getItem(storageKey(brandId)) ?? "{}");
  } catch {
    return {};
  }
}

function writeState(brandId: string, state: StoredState) {
  try {
    localStorage.setItem(storageKey(brandId), JSON.stringify(state));
  } catch {
    // Storage blocked: the prompt just comes back on the next page.
  }
}

export function useFounderCallPrompt() {
  const { brandDetails } = useBrand();
  const [isModalOpen, setIsModalOpen] = useState(false);
  useAutoPromptRelease(PROMPT_ID, isModalOpen);

  const brandId = brandDetails?.id;
  const createdAt = brandDetails?.createdAt;
  // Dev stores already get the dev-store upgrade modal, which offers a call.
  const isDevStore = Boolean(brandDetails?.isDevStore);

  useEffect(() => {
    if (!brandId || !createdAt || isDevStore) return;
    // Written as !(<) so an unparseable date (NaN) also opts out.
    if (!(Date.now() - new Date(createdAt).getTime() < ELIGIBLE_MS)) return;
    const { booked, snoozedUntil } = readState(brandId);
    if (booked || (snoozedUntil ?? 0) > Date.now()) return;

    let quietSince: number | null = null;
    const interval = setInterval(() => {
      // Never follow another self-opening prompt in the same visit.
      if (hasOtherAutoPromptShown(PROMPT_ID)) {
        clearInterval(interval);
        return;
      }
      if (document.hidden || isOverlayOpen() || isAutoPromptActive()) {
        quietSince = null;
        return;
      }
      quietSince ??= Date.now();
      if (Date.now() - quietSince < QUIET_MS) return;
      if (claimAutoPrompt(PROMPT_ID)) {
        clearInterval(interval);
        setIsModalOpen(true);
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [brandId, createdAt, isDevStore]);

  // The modal's link opens Calendly; this only records the choice.
  const handleBookCall = useCallback(() => {
    if (brandId) writeState(brandId, { booked: true });
    setIsModalOpen(false);
  }, [brandId]);

  // Also used for the close button, Escape and a backdrop click.
  const handleMaybeLater = useCallback(() => {
    if (brandId) writeState(brandId, { snoozedUntil: Date.now() + SNOOZE_MS });
    setIsModalOpen(false);
  }, [brandId]);

  return { isModalOpen, handleBookCall, handleMaybeLater };
}
